/**
 * Layer Types Enum
 *
 * Defines valid SVG layer types for the interactive site map.
 * Provides singleton instances for each layer, matching keys in constants.SVG_LAYER_VISIBILITY.
 * Prevents magic strings and ensures type consistency across the feature.
 * Uses singleton pattern for instance management.
 *
 * @module nativeInteractiveSiteMap/enums/layerTypes
 */

class LayerTypes {
  static instances = {};

  /**
   * Street Names label layer — visible at zoom 16+
   */
  static get StreetNames() {
    return this.getInstance('StreetNames');
  }

  /**
   * Lot Numbers label layer — visible at zoom 16+
   */
  static get LotNumbers() {
    return this.getInstance('LotNumbers');
  }

  /**
   * Amenity Names label layer — visible at zoom 16+
   */
  static get AmenityNames() {
    return this.getInstance('AmenityNames');
  }

  /**
   * Building Names label layer — visible at zoom 16+
   */
  static get BuildingNames() {
    return this.getInstance('BuildingNames');
  }

  /**
   * Lot Polygons base layer
   */
  static get LotPolygons() {
    return this.getInstance('LotPolygons');
  }

  /**
   * Grass areas base layer
   */
  static get Grass() {
    return this.getInstance('Grass');
  }

  /**
   * Street elements base layer
   */
  static get Street() {
    return this.getInstance('Street');
  }

  /**
   * Amenity elements base layer
   */
  static get Amenity() {
    return this.getInstance('Amenity');
  }

  /**
   * Buildings base layer
   */
  static get Buildings() {
    return this.getInstance('Buildings');
  }

  /**
   * Pool features base layer
   */
  static get Pool() {
    return this.getInstance('Pool');
  }

  /**
   * Golf course features base layer
   */
  static get Golf() {
    return this.getInstance('Golf');
  }

  /**
   * Wetland features base layer
   */
  static get Wetland() {
    return this.getInstance('Wetland');
  }

  /**
   * Landscaping features base layer
   */
  static get Landscaping() {
    return this.getInstance('Landscaping');
  }

  /**
   * Walkway elements base layer
   */
  static get Walkway() {
    return this.getInstance('Walkway');
  }

  /**
   * Water features base layer
   */
  static get Water() {
    return this.getInstance('Water');
  }

  /**
   * Sand areas base layer
   */
  static get Sand() {
    return this.getInstance('Sand');
  }

  /**
   * Curb elements base layer
   */
  static get Curbs() {
    return this.getInstance('Curbs');
  }

  /**
   * Lot Group features base layer
   */
  static get LotGroup() {
    return this.getInstance('LotGroup');
  }

  /**
   * Get layer type instance by string key.
   * @param {string} key - Layer type key name
   * @returns {LayerTypes|null} Layer type instance or null if key not found
   */
  static fromKey(key) {
    const map = {
      StreetNames:   LayerTypes.StreetNames,
      LotNumbers:    LayerTypes.LotNumbers,
      AmenityNames:  LayerTypes.AmenityNames,
      BuildingNames: LayerTypes.BuildingNames,
      LotPolygons:   LayerTypes.LotPolygons,
      Grass:         LayerTypes.Grass,
      Street:        LayerTypes.Street,
      Amenity:       LayerTypes.Amenity,
      Buildings:     LayerTypes.Buildings,
      Pool:          LayerTypes.Pool,
      Golf:          LayerTypes.Golf,
      Wetland:       LayerTypes.Wetland,
      Landscaping:   LayerTypes.Landscaping,
      Walkway:       LayerTypes.Walkway,
      Water:         LayerTypes.Water,
      Sand:          LayerTypes.Sand,
      Curbs:         LayerTypes.Curbs,
      LotGroup:      LayerTypes.LotGroup,
    };
    return map[key] ?? null;
  }

  /**
   * Get or create singleton instance for a layer type.
   * @private
   * @param {string} key - Layer type key
   * @returns {LayerTypes} Singleton instance
   */
  static getInstance(key) {
    if (!this.instances[key]) {
      this.instances[key] = new LayerTypes(key);
    }
    return this.instances[key];
  }

  /**
   * Constructor for layer type instance.
   * @param {string} name - The layer type name
   */
  constructor(name) {
    this.name = name;
  }

  /**
   * Get string representation of layer type.
   * @returns {string} The layer type name
   */
  toString() {
    return this.name;
  }
}

module.exports = LayerTypes;
